/**
 * Created by rathompson on 9/14/22
 * File: SupportInfoModal
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useMemo, useEffect, useRef } from 'react'
import {
  Button,
  CopyButton,
  Bold,
  Body,
  noop,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from '@panwds/react-ui'
import {
  BUTTON_COPY,
  BUTTON_CLOSE,
  LABEL_PRODUCT_INFO,
  ERROR_RESOLUTION_SUBTEXT, LINK_SUPPORT_CASE,
} from '../../../constants/LabelConstants'
import StringTemplate from '../../../../common/utils/StringTemplate'
import { BUNDLED_PRODUCTS } from '../../../../common/constants/LabelConstants'

export const SupportInfoModal = ({ closeModal: onClose, isOpen, instance }) => {
  const [table, copyText, copyButton] = useMemo(() => {
    if (!instance) {
      return []
    }
    const { tsg_id, tenant_id, csp_tenant_id, contract, support_account_id, license_id, app_id } = instance
    const serial_numbers = BUNDLED_PRODUCTS.has(app_id) ? instance?.entitlements?.map(e => e.serial_number)?.join(', ') : instance?.serial_number
    const info = { tsg_id, tenant_id, csp_tenant_id, support_account_id, license_id, serial_numbers }
    const fields = [
      ['TSG ID', tsg_id],
      ['CSP Account ID', support_account_id],
      ['CSP Tenant ID', csp_tenant_id],
      ['Instance ID', tenant_id],
      ['Serial Number', (contract || serial_numbers)],
      ['License ID', license_id],
    ].filter(field => field[1])

    const copyText = [...fields, ['DETAILS', JSON.stringify(info, null, 2)]].map(([name, value]) => `${name}: ${value}`).join('\n')
    const table = (
      <table className="cm-w-full cm-mt-2 cm-mb-4" style={{ tableLayout: 'fixed' }}>
        <tbody>
          {fields.map(([name, value]) => (
            <tr key={name}>
              <td className="cm-align-top"><strong>{name}</strong></td>
              <td className="cm-align-top" style={{ width: '75%' }}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )
    const copyButton = (
      <CopyButton
        text={copyText}
        appearance="secondary"
      >{BUTTON_COPY}</CopyButton>
    )
    return [table, copyText, copyButton]
  }, [instance])

  const copyRef = useRef(copyText)
  copyRef.current = copyText

  useEffect(() => {
    const copyListener = (e) => {
      const selection = document.getSelection()?.toString()
      const override = !selection?.length || (
        selection.length > 10 && selection.includes('Provisioning Status') && selection.includes('Instance Status')
      )
      if (copyRef.current && override) {
        e.preventDefault()
        e.clipboardData?.setData('text/plain', copyRef.current)
      }
    }
    document.addEventListener('copy', copyListener)
    return () => {
      document.removeEventListener('copy', copyListener)
    }
  }, [])

  useEffect(() => {
    const modalMask = isOpen ?
      document.querySelector('.tw-bg-gray-900.tw-fixed.tw-inset-0.tw-z-10.tw-transition-opacity.tw-duration-200.tw-opacity-75') ||
      document.querySelector('.tw-bg-blue-steel-700.tw-fixed.tw-inset-0.tw-z-10.tw-transition-opacity.tw-duration-200.tw-opacity-80') :
      null
    if (modalMask) {
      modalMask.style.zIndex = String(1052)
    }
  }, [isOpen])

  return (
    <Modal {...{ onClose, isOpen }} size="md" center style={{ zIndex: 1052, top: '50%', '--tw-translate-y': '-50%' }}>
      <ModalHeader title="View Support Information"/>
      <ModalBody addClassName="cm-pb-0">
        <Bold>{LABEL_PRODUCT_INFO}</Bold>
        {table}
        <Body addClassName="cm-mt-2 cm-mb-2" size="lg">{StringTemplate(ERROR_RESOLUTION_SUBTEXT, (
          <><br/>
            <a
              href="https://supportcases.paloaltonetworks.com/"
              target="_blank"
              rel="noopener noreferrer">{LINK_SUPPORT_CASE}</a>
          </>
        ))}</Body>
      </ModalBody>
      <ModalFooter appearance="clear" secondaryContent={copyButton}>
        <Button onClick={onClose} appearance="primary">
          {BUTTON_CLOSE}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export const useSupportInfoModule = (instance) => {
  const { openModal, closeModal } = useModal(SupportInfoModal)
  return useMemo(() => ({
    openModal: instance ? openModal.bind(undefined, { instance }) : noop,
    closeModal,
  }), [openModal, instance, closeModal])
}

export default SupportInfoModal

